import { useRef, useState, type KeyboardEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslate } from "ra-core";
import { AlertCircle, FileText, Loader2, Send } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

import type { Contact, ContactConversationMessage } from "../types";
import { buildOptimisticOperatorMessage } from "../whatsapp/inbox/conversationPages";
import {
  conversationQueryKey,
  invalidateInboxQueries,
  useInboxEnabled,
} from "../whatsapp/inbox/useInbox";
import { senderLabel } from "../whatsapp/whatsappSenderSelection";
import { ContactWhatsAppActions } from "./ContactWhatsAppActions";
import {
  MAX_WHATSAPP_TEXT_LENGTH,
  blockReasonText,
  useSendWhatsAppMessage,
} from "./useSendWhatsAppMessage";
import { WhatsAppMarketingOptOutNotice } from "./WhatsAppMarketingOptOutNotice";

/**
 * In-thread reply box rendered as the chat footer. Freeform text goes straight
 * out (Enter sends, Shift+Enter inserts a newline); when the 24h window is
 * closed it hands over to the template dialog instead. Marketing opt-out never
 * closes the window (it only gates templates and broadcasts), so it is shown
 * as a hint rather than a block.
 */
export const WhatsAppComposer = ({
  contact,
  onSent,
}: {
  contact: Contact;
  /** Receives an optimistic outbound row right after a freeform send. */
  onSent?: (message: ContactConversationMessage) => void;
}) => {
  const translate = useTranslate();
  const t = (key: string, options?: Record<string, unknown>) =>
    translate(`crm.whatsapp.inbox.composer.${key}`, options);
  const queryClient = useQueryClient();
  const inboxEnabled = useInboxEnabled();
  const send = useSendWhatsAppMessage({ contactId: contact.id });
  const [body, setBody] = useState("");
  const [keepOpen, setKeepOpen] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const refreshThread = () => {
    void queryClient.invalidateQueries({
      queryKey: conversationQueryKey(contact.id),
    });
    void invalidateInboxQueries(queryClient);
  };

  const submit = async () => {
    const text = body.trim();
    if (!text || send.sending || text.length > MAX_WHATSAPP_TEXT_LENGTH) return;
    const outcome = await send.sendText(text, { keepOpen });
    if (outcome.status !== "sent") return;
    setBody("");
    onSent?.(
      buildOptimisticOperatorMessage({
        contactId: contact.id,
        whatsappUserId:
          contact.whatsapp_user_id ?? send.capability?.whatsapp_user_id,
        body: text,
        idempotencyKey: outcome.idempotencyKey,
        senderKey: send.senderKey,
        providerMessageId: outcome.result.provider_message_id ?? null,
        occurredAt: outcome.sentAt,
      }),
    );
    refreshThread();
    textareaRef.current?.focus();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (
      event.key === "Enter" &&
      !event.shiftKey &&
      !event.nativeEvent.isComposing
    ) {
      event.preventDefault();
      void submit();
    }
  };

  const remaining = MAX_WHATSAPP_TEXT_LENGTH - body.length;

  return (
    <div className="space-y-2">
      {send.selectableSenders.length > 1 ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>{t("sender")}</span>
          <Select value={send.senderKey} onValueChange={send.setSenderKey}>
            <SelectTrigger size="sm" className="h-7 w-auto min-w-40 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {send.selectableSenders.map((sender) => (
                <SelectItem key={sender.sender_key} value={sender.sender_key}>
                  {senderLabel(sender)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : null}

      {send.canSendFreeform ? (
        <div className="flex items-end gap-2">
          <Textarea
            ref={textareaRef}
            value={body}
            onChange={(event) => setBody(event.target.value)}
            onKeyDown={onKeyDown}
            placeholder={t("placeholder")}
            disabled={send.sending}
            rows={2}
            aria-label={t("placeholder")}
            className="max-h-40 min-h-[44px] resize-y bg-white text-[14px] dark:bg-[#2a3942]"
          />
          <Button
            type="button"
            size="icon"
            className="size-10 shrink-0 rounded-full"
            aria-label={t("send")}
            onClick={() => void submit()}
            disabled={!body.trim() || send.sending || remaining < 0}
          >
            {send.sending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Send className="size-4" />
            )}
          </Button>
        </div>
      ) : send.capability ? (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200">
          <span className="flex min-w-0 items-start gap-2">
            <AlertCircle className="mt-0.5 size-4 shrink-0" />
            <span>
              <span className="font-medium">{t("blocked")}</span>
              {" · "}
              {blockReasonText(send.blockReasons, translate)}
            </span>
          </span>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={() => setTemplateOpen(true)}
          >
            <FileText className="size-4" />
            {t("send_template")}
          </Button>
        </div>
      ) : (
        <p className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="size-3 animate-spin" />
          {t("checking")}
        </p>
      )}

      <WhatsAppMarketingOptOutNotice capability={send.capability} />

      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
        {inboxEnabled ? (
          <label className="flex cursor-pointer items-center gap-2">
            <Checkbox
              checked={keepOpen}
              onCheckedChange={(value) => setKeepOpen(value === true)}
              aria-label={t("keep_open")}
            />
            <span title={t("keep_open_hint")}>{t("keep_open")}</span>
          </label>
        ) : (
          <span />
        )}
        {send.canSendFreeform ? (
          <span className="tabular-nums">{remaining}</span>
        ) : null}
      </div>

      <ContactWhatsAppActions
        contact={contact}
        open={templateOpen}
        onOpenChange={setTemplateOpen}
        showTrigger={false}
        keepOpen={keepOpen}
        onSent={refreshThread}
      />
    </div>
  );
};
